/**
 * Life tab API — milestones, achievements, year stats.
 * All queries use .throwOnError() per project rules.
 */

import { z } from 'zod'
import { supabase } from '@turnifly/shared/api'

// ── Famous milestones ──

export const FamousMilestoneSchema = z.object({
  id: z.number(),
  age: z.number(),
  person: z.string(),
  achievement_uz: z.string(),
  achievement_en: z.string(),
  achievement_ru: z.string().nullable().catch(null),
  category: z.string().nullable().catch(null),
})

export type FamousMilestone = z.infer<typeof FamousMilestoneSchema>

export async function fetchFamousMilestones(): Promise<FamousMilestone[]> {
  const { data } = await supabase
    .from('famous_milestones')
    .select('*')
    .order('age')
    .throwOnError()

  return z.array(FamousMilestoneSchema).parse(data ?? [])
}

// ── User achievements for timeline ──

export interface TimelineEvent {
  type: 'goal' | 'streak' | 'challenge'
  icon: string
  label: string
  date: string
  age: number
}

export async function fetchLifeAchievements(
  userId: string,
  birthDate: string,
): Promise<TimelineEvent[]> {
  const birthYear = new Date(birthDate).getFullYear()
  const events: TimelineEvent[] = []

  // Parallel fetch: goals + streaks + challenges
  const [goalsRes, streaksRes, challengesRes] = await Promise.all([
    supabase
      .from('goals')
      .select('icon, name, achieved_at')
      .eq('user_id', userId)
      .eq('status', 'achieved')
      .not('achieved_at', 'is', null)
      .order('achieved_at')
      .throwOnError(),
    supabase
      .from('habit_streaks')
      .select('longest_streak, last_completed_date')
      .eq('user_id', userId)
      .gte('longest_streak', 30)
      .throwOnError(),
    supabase
      .from('challenge_members')
      .select('challenge_id, completed_at, challenges(name)')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .not('completed_at', 'is', null)
      .throwOnError(),
  ])

  // Goals
  for (const g of (goalsRes.data ?? []) as Array<{ icon: string; name: string; achieved_at: string }>) {
    const year = new Date(g.achieved_at).getFullYear()
    events.push({
      type: 'goal',
      icon: g.icon ?? '🎯',
      label: g.name,
      date: g.achieved_at,
      age: year - birthYear,
    })
  }

  // Streaks (30+ days)
  for (const s of (streaksRes.data ?? []) as Array<{ longest_streak: number; last_completed_date: string | null }>) {
    if (!s.last_completed_date) continue
    const year = new Date(s.last_completed_date).getFullYear()
    events.push({
      type: 'streak',
      icon: '🔥',
      label: `${s.longest_streak} kun streak`,
      date: s.last_completed_date,
      age: year - birthYear,
    })
  }

  // Challenges completed
  for (const c of (challengesRes.data ?? []) as Array<Record<string, unknown>>) {
    const completedAt = c.completed_at as string | null
    if (!completedAt) continue
    const year = new Date(completedAt).getFullYear()
    const challengeData = c.challenges as Record<string, unknown> | Array<Record<string, unknown>> | null
    const name = Array.isArray(challengeData) ? (challengeData[0]?.name as string ?? 'Challenge') : ((challengeData as Record<string, unknown> | null)?.name as string ?? 'Challenge')
    events.push({
      type: 'challenge',
      icon: '🏆',
      label: name,
      date: completedAt,
      age: year - birthYear,
    })
  }

  events.sort((a, b) => a.date.localeCompare(b.date))
  return events
}

// ── Year stats (for year detail sheet) ──

export interface YearStats {
  year: number
  habitsActive: number
  habitCompletionDays: number
  goalsAchieved: number
  challengesCompleted: number
  focusSessions: number
  focusMinutes: number
  longestStreak: number
}

export async function fetchYearStats(userId: string, year: number): Promise<YearStats> {
  const yearStart = `${year}-01-01`
  const yearEnd = `${year}-12-31`

  const [habitsRes, logsRes, goalsRes, challengesRes, pomodoroRes, streakRes] = await Promise.all([
    supabase
      .from('habits')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .lte('created_at', yearEnd + 'T23:59:59')
      .or(`deleted_at.is.null,deleted_at.gte.${yearStart}`)
      .throwOnError(),
    supabase
      .from('habit_logs')
      .select('log_date', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('completed', true)
      .gte('log_date', yearStart)
      .lte('log_date', yearEnd)
      .throwOnError(),
    supabase
      .from('goals')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'achieved')
      .gte('achieved_at', yearStart)
      .lte('achieved_at', yearEnd + 'T23:59:59')
      .throwOnError(),
    supabase
      .from('challenge_members')
      .select('challenge_id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'completed')
      .gte('completed_at', yearStart)
      .lte('completed_at', yearEnd + 'T23:59:59')
      .throwOnError(),
    supabase
      .from('pomodoro_sessions')
      .select('actual_seconds')
      .eq('user_id', userId)
      .eq('session_type', 'work')
      .eq('completed', true)
      .gte('started_at', yearStart)
      .lte('started_at', yearEnd + 'T23:59:59')
      .throwOnError(),
    // Longest streak in this year
    supabase
      .from('habit_streaks')
      .select('longest_streak')
      .eq('user_id', userId)
      .order('longest_streak', { ascending: false })
      .limit(1)
      .throwOnError(),
  ])

  const pomData = pomodoroRes.data ?? []
  const totalSeconds = pomData.reduce(
    (sum: number, s: Record<string, unknown>) => sum + (Number(s.actual_seconds) || 0), 0,
  )

  const streakRows = (streakRes as { data: Array<{ longest_streak: number }> | null }).data ?? []
  const longestStreak = streakRows[0]?.longest_streak ?? 0

  return {
    year,
    habitsActive: habitsRes.count ?? 0,
    habitCompletionDays: logsRes.count ?? 0,
    goalsAchieved: goalsRes.count ?? 0,
    challengesCompleted: challengesRes.count ?? 0,
    focusSessions: pomData.length,
    focusMinutes: Math.round(totalSeconds / 60),
    longestStreak,
  }
}

// ── Update life settings ──

export async function updateLifeSettings(
  userId: string,
  settings: { birth_date?: string; life_expectancy?: number },
): Promise<void> {
  await supabase
    .from('profiles')
    .update({ ...settings, updated_at: new Date().toISOString() })
    .eq('id', userId)
    .throwOnError()
}
